import { useState } from 'react';
import { baseUrl } from 'src/Tenants';
import { apiReq, createQueryParameters } from '../../client';
import { toast } from 'react-toastify';
import {
  ApiResponse,
  IDashboardAgeOverview,
  IDashboardFilters,
  IDashboardStatusOverview,
  IServiceRequest,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';

// Legacy constants for age-based filters.
export const serviceRequestAgeEnum = {
  olderThan60Days: 1,
  between30And60Days: 2,
  daysLessThan30: 3,
};

// Removes specified parameters from filter object.
const filterParams = (
  filters: IDashboardFilters,
  paramsToExclude: string[]
): IDashboardFilters => {
  const filteredParams: Partial<IDashboardFilters> = {};

  for (const key in filters) {
    const filterIsOwnProperty: boolean = filters.hasOwnProperty(key);
    const filterIsNotExcluded: boolean = !paramsToExclude.includes(key);

    if (filterIsOwnProperty && filterIsNotExcluded) {
      filteredParams[key] = filters[key];
    }
  }

  return filteredParams as IDashboardFilters;
};

export const serviceRequestDashboardApi = {
  // Fetches list of service requests.
  list: (filters: IDashboardFilters) =>
    apiReq(
      baseUrl +
        'api/ServiceRequest/dashboard-list' +
        createQueryParameters(filters),
      'GET',
      true,
      null,
      false
    ),
  // Fetches status counts without status/age filters.
  statusOverview: (filters: IDashboardFilters) => {
    const filtersToExcludeFromRequest: string[] = [
      'Statuses',
      'ServiceRequestAge',
    ];
    const filteredParams: IDashboardFilters = filterParams(
      filters,
      filtersToExcludeFromRequest
    );

    return apiReq(
      baseUrl +
        'api/ServiceRequest/dashboard-status-overview' +
        createQueryParameters(filteredParams),
      'GET',
      true,
      null,
      false
    );
  },
  // Fetches age breakdown with status filter support.
  ageOverview: (filters: IDashboardFilters) => {
    const filtersToExcludeFromRequest: string[] = ['ServiceRequestAge'];
    const filteredParams: IDashboardFilters = filterParams(
      filters,
      filtersToExcludeFromRequest
    );

    return apiReq(
      baseUrl +
        'api/ServiceRequest/dashboard-age-overview' +
        createQueryParameters(filteredParams),
      'GET',
      true,
      null,
      false
    );
  },
};

export const useServiceRequestDashboardApi = () => {
  const [serviceRequests, setServiceRequests] = useState<IServiceRequest[]>([]);
  const [statusOverview, setStatusOverview] =
    useState<IDashboardStatusOverview>({} as IDashboardStatusOverview);
  const [ageOverview, setAgeOverview] = useState<IDashboardAgeOverview>(
    {} as IDashboardAgeOverview
  );
  const [loading, setLoading] = useState<boolean>(true);

  // Fetches and sets service request data from the API.
  const fetchDashboard = async (filters?: IDashboardFilters) => {
    try {
      setLoading(true);
      const res: any = await serviceRequestDashboardApi.list(filters);

      if (res?.status === 200) {
        const resJSON: ApiResponse<IServiceRequest[]> = await res.json();
        const data: IServiceRequest[] = resJSON?.data;
        setServiceRequests(data || []);
      } else {
        toast.error(
          'Failed to fetch service requests, please try again later.',
          {
            autoClose: 5000,
            hideProgressBar: true,
            position: 'top-center',
          }
        );
      }
    } catch (error) {
      toast.error('Failed to fetch service requests, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetches and sets status counts from the API.
  const fetchStatusOverview = async (filters: IDashboardFilters) => {
    try {
      const res: any = await serviceRequestDashboardApi.statusOverview(filters);
      const resJSON: ApiResponse<IDashboardStatusOverview> = await res.json();
      const data: IDashboardStatusOverview = resJSON.data;
      setStatusOverview(data);
    } catch (error) {
      toast.error('Failed to fetch status overview, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetches and sets age breakdowns from the API.
  const fetchAgeOverview = async (filters: IDashboardFilters) => {
    try {
      const res: any = await serviceRequestDashboardApi.ageOverview(filters);
      const resJSON: ApiResponse<IDashboardAgeOverview> = await res.json();
      const data: IDashboardAgeOverview = resJSON.data;
      setAgeOverview(data);
    } catch (error) {
      toast.error('Failed to fetch age overview, please try again later.', {
        autoClose: 5000,
        hideProgressBar: true,
        position: 'top-center',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetches both status and age data simultaneously from the API.
  const fetchAllOverviews = async (filters: IDashboardFilters) => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStatusOverview(filters),
        fetchAgeOverview(filters),
      ]);
    } catch (error) {
      toast.error(
        'Failed to fetch dashboard overviews, please try again later.',
        {
          autoClose: 5000,
          hideProgressBar: true,
          position: 'top-center',
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    serviceRequests,
    statusOverview,
    ageOverview,
    loading,
    fetchDashboard,
    fetchStatusOverview,
    fetchAgeOverview,
    fetchAllOverviews,
  };
};
